import chartExportCore from './chartExportCore';
import { removeWatermarkOption, restoreWatermarkOption } from './chartWatermark';

const core = chartExportCore.buildSheetsFromOption
  ? chartExportCore
  : (chartExportCore.default || {});
const { buildSheetsFromOption, safeFilename } = core;

const TITLE_FONT = '"PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif';

let xlsxPromise = null;

export function prefetchXlsx() {
  if (!xlsxPromise) xlsxPromise = import('xlsx');
  return xlsxPromise;
}

function wrapLines(ctx, text, maxWidth) {
  const chars = Array.from(text || '');
  const lines = [];
  let line = '';
  chars.forEach((char) => {
    const next = line + char;
    if (line && ctx.measureText(next).width > maxWidth) {
      lines.push(line);
      line = char;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function chartCanvas(chart) {
  const dom = chart && typeof chart.getDom === 'function' ? chart.getDom() : null;
  const source = dom ? dom.querySelector('canvas') : null;
  if (!source || !source.width || !source.height) return null;
  return source;
}

function flushChart(chart) {
  const zr = chart.getZr && chart.getZr();
  if (zr && typeof zr.refreshImmediately === 'function') zr.refreshImmediately();
}

function copyCanvas(source) {
  const copy = document.createElement('canvas');
  copy.width = source.width;
  copy.height = source.height;
  copy.getContext('2d').drawImage(source, 0, 0);
  return copy;
}

function pixelsForExport(chart, withWatermark) {
  const live = chartCanvas(chart);
  if (!live) return null;
  if (withWatermark) return live;

  chart.setOption(removeWatermarkOption());
  flushChart(chart);
  try {
    const current = chartCanvas(chart);
    if (!current) return null;
    return copyCanvas(current);
  } finally {
    chart.setOption(restoreWatermarkOption());
    flushChart(chart);
  }
}

export function buildChartPngDataUrl(chart, { title, withWatermark }) {
  const source = pixelsForExport(chart, withWatermark);
  if (!source) {
    throw new Error('图表尚未加载完成');
  }

  const scale = source.width / (source.clientWidth || source.width) || 1;
  const fontSize = Math.round(18 * scale);
  const measure = document.createElement('canvas').getContext('2d');
  measure.font = `bold ${fontSize}px ${TITLE_FONT}`;
  const maxTextWidth = Math.max(40, source.width - Math.round(48 * scale));
  const lines = title ? wrapLines(measure, title, maxTextWidth) : [];
  const lineHeight = Math.round(fontSize * 1.4);
  const titlePad = lines.length ? Math.round(16 * scale) : 0;
  const titleHeight = lines.length ? titlePad * 2 + lines.length * lineHeight : 0;

  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height + titleHeight;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (lines.length) {
    ctx.fillStyle = '#2e3968';
    ctx.font = `bold ${fontSize}px ${TITLE_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    lines.forEach((line, index) => {
      ctx.fillText(line, canvas.width / 2, titlePad + lineHeight / 2 + index * lineHeight);
    });
  }

  ctx.drawImage(source, 0, titleHeight);
  return canvas.toDataURL('image/png');
}

function triggerDownload(href, filename) {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function columnWidth(column, rows, index) {
  const widthOf = (value) => Array.from(String(value ?? '')).reduce((sum, char) => (
    sum + (char.charCodeAt(0) > 255 ? 2 : 1)
  ), 0);
  const longest = rows.reduce((max, row) => Math.max(max, widthOf(row[index])), widthOf(column));
  return { wch: Math.min(40, Math.max(10, longest + 2)) };
}

function writeWorkbook(XLSX, filename, sheets) {
  const workbook = XLSX.utils.book_new();
  sheets.forEach((sheet) => {
    const worksheet = XLSX.utils.aoa_to_sheet([sheet.columns, ...sheet.rows]);
    worksheet['!cols'] = sheet.columns.map((column, index) => columnWidth(column, sheet.rows, index));
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
  });
  XLSX.writeFile(workbook, filename);
}

function resolveChart(chart) {
  if (!chart || typeof chart.getDom !== 'function') return null;
  try {
    return chart.getDom() ? chart : null;
  } catch (error) {
    return null;
  }
}

function resolveXlsx(module) {
  if (module && module.utils && module.writeFile) return module;
  if (module && module.default && module.default.utils) return module.default;
  return module;
}

export async function exportChart({ chart, title, withWatermark, includeImage, includeData }) {
  const instance = resolveChart(chart);
  if (!instance) {
    throw new Error('图表尚未加载完成');
  }

  const filename = safeFilename(title);
  let sheets = null;
  if (includeData) {
    let option = null;
    try {
      option = instance.getOption();
    } catch (error) {
      throw new Error('图表尚未加载完成');
    }
    sheets = buildSheetsFromOption(option);
    if (!sheets.length) {
      throw new Error('没有可下载的数据');
    }
  }

  if (includeImage) {
    triggerDownload(
      buildChartPngDataUrl(instance, { title, withWatermark }),
      `${filename}图表.png`
    );
  }
  if (sheets) {
    const XLSX = resolveXlsx(await prefetchXlsx());
    writeWorkbook(XLSX, `${filename}数据表.xlsx`, sheets);
  }
}
